<template>
  <div data-page-name="error">
    <TheHeader />

    <main>
      <section>
        <AppWrapper>
          <div class="left">
            <h1>{{ $t('Got_Veeam') }}?</h1>
            <p>{{ $t('error.description') }}</p>

            <Buttons.Link :to="localePathWithSlash('/')">
              {{ $t('Visit_Home_Page') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Link>
          </div>

          <div class="right">
            <NuxtImg
              src="/redesign/images/ootbi/ootbi-cartoon-404.webp"
              alt="Object First cartoon character with magnifying glass between two numbers four"
              width="697"
              height="417"
              sizes="xs:328px md:697px"
              densities="x1 x2"
              format="webp"
            />
          </div>
        </AppWrapper>
      </section>

      <ClientOnly>
        <div
          v-if="env === 'development' && error"
          style="
            border: 1px solid red;
            max-width: 80%;
            max-height: 300px;
            overflow: auto;
            margin: 0 auto;
          "
        >
          <pre>{{ error }}</pre>

          <div v-if="error.stack" v-html="error.stack" style="color: red" />
        </div>
      </ClientOnly>
    </main>

    <TheFooter />
  </div>

  <AppOverlay id="overlay" v-bind="overlay">
    <transition name="fade" mode="out-in">
      <TheSearch v-if="isSearchVisible" @close="closeSearch" />
    </transition>
  </AppOverlay>

  <ClientOnly>
    <transition name="slide-from-right">
      <Menu.Mobile v-if="isMobileMenuVisible" />
    </transition>
  </ClientOnly>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import type { NuxtError } from '#app';

// components
import TheHeader from '~/components/TheHeader/TheHeader.vue';
import AppWrapper from '~/components/redesign/AppWrapper.vue';
import Buttons from '~/components/redesign/Buttons';
import TheFooter from '~/components/redesign/TheFooter.vue';
import AppOverlay from '~/components/redesign/AppOverlay.vue';
import Menu from '~/components/Menu';

// composable
import useReport from '~/composables/useReport';
import onRouteChange from '~/composables/onRouteChange';

// stores
import useUIStore from '~/stores/ui';
import TheSearch from '~/components/redesign/TheSearch/Container.vue';

const { error } = defineProps<{ error: NuxtError }>();

const uiStore = useUIStore();
const { setOverlay, setMobileMenuVisibility, setSearchVisibility } = uiStore;
const { isMobileMenuVisible, isSearchVisible, isScrollLocked, overlay } =
  storeToRefs(uiStore);

const closeSearch = () => {
  setOverlay({ visible: false });
  setSearchVisibility(false);
};

onRouteChange(() => setMobileMenuVisibility(false));

const localePathWithSlash = useLocalePathWithSlash();
const {
  public: { env },
} = useRuntimeConfig();

useHead({
  bodyAttrs: {
    class: {
      'no-scroll': isScrollLocked,
    },
  },
});
useReport(error, document.location.href);
</script>

<style scoped lang="scss">
@use '_/mixins/media';
@use '_/mixins/size';
@use '_/mixins/flex';
@use '_/mixins/typo';
@use '_/mixins/position';
@use '_/transitions';
@use '_/fn';

[data-page-name='error'] {
  @include flex.stretch-stretch;
  flex-direction: column;
  min-height: 100vh;

  main {
    @include flex.center-center;
    flex: 1;
    padding: 8rem 0;

    @include media.tablet {
      padding: 5rem 0 8rem 0;
    }

    @include media.mobile {
      padding: 5rem 0;
    }

    section {
      @include flex.start-sb;
      gap: 0 2rem;

      @include media.tablet {
        flex-direction: column;
        gap: 1.5rem 0;
      }

      @include media.mobile {
        gap: 2.5rem 0;
      }

      .left {
        padding-top: 6.94rem;

        @include media.tablet {
          padding-top: 0;
        }

        @include media.mobile {
          width: 100%;
        }

        h1 {
          @include typo.h2;
          margin-bottom: 1rem;

          @include media.mobile {
            margin-bottom: 0.5rem;
          }
        }

        p {
          @include typo.body-2;
          margin-bottom: 2rem;

          @include media.mobile {
            margin-bottom: 1.5rem;
          }
        }

        a {
          display: block;
          width: max-content;

          @include media.mobile {
            width: 100%;
          }
        }
      }

      .right {
        @include media.tablet {
          margin: 0 auto;
        }

        @include media.mobile {
          width: 100%;
        }

        img {
          display: block;
          width: 100%;
          height: auto;
        }
      }
    }
  }

  [data-component-name='TheHeader'] {
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    z-index: 6;
  }

  [data-component-name='BreakingNewsBanner'] {
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    z-index: 5;
  }
}

[data-component-name='TheSearch'] {
  position: absolute;
  top: 5.5rem;

  @include size.fluid-max-width(75rem);
  max-height: calc(100dvh - (5.5rem + 1.5rem));

  @include media.tablet {
    @include size.fluid-max-width(calc(100% - 4rem));
  }

  @include media.mobile {
    top: 3.5rem;

    @include size.fluid-max-width(100%);
    max-height: calc(100dvh - (3.5rem + 1rem));
  }
}

[data-component-name='Menu.Mobile'] {
  @include position.fixed-full;
  z-index: 2;

  top: 68px;

  @include media.mobile {
    top: 54px;
  }
}
</style>
